import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useUser } from '../context/UserContext';
import { useBrand } from '../context/BrandContext';
import {
  User,
  Video,
  Settings,
  LogOut,
  Shield,
  ShieldCheck,
  Star,
  ChevronRight,
  MapPin,
  Clock,
  ExternalLink,
  Lock,
  Play,
  Navigation,
  ShoppingBag,
  Users,
  Compass,
  Zap,
  Crown,
  IdCard,
  Gem,
  Edit2,
  Save,
  X,
  CheckCircle2,
  Copy,
  ArrowRight,
  Calendar,
  Map as MapIcon,
  Moon,
  MessageSquare,
  Camera,
  Loader2,
  Image as ImageIcon,
  Mail,
  FileText,
  Package
} from 'lucide-react';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { Review } from '../types';

export const MemberPortal: React.FC = () => {
  const location = useLocation();
  const { user, profile, signOut, updateProfile, isAdmin, loading } = useUser();
  const {
    explorerTV,
    resolveMediaUrl,
    stories,
    trackingConfig,
    userBookings,
    orders,
    membershipPlans,
    cancelBooking,
    expeditionStops,
    expeditionSlots,
    uploadFile
  } = useBrand();
  const [isEditingProfile, setIsEditingProfile] = React.useState(false);
  const [newDisplayName, setNewDisplayName] = React.useState(profile?.displayName || '');
  const [showInviteToast, setShowInviteToast] = React.useState(false);
  const [cancellingBookingId, setCancellingBookingId] = React.useState<string | null>(null);
  const [confirmCancelBookingId, setConfirmCancelBookingId] = React.useState<string | null>(null);
  const [reviewContent, setReviewContent] = React.useState('');
  const [reviewImage, setReviewImage] = React.useState<File | null>(null);
  const [reviewImageUrl, setReviewImageUrl] = React.useState<string | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = React.useState(false);
  const [showReviewToast, setShowReviewToast] = React.useState(false);
  const [myReviews, setMyReviews] = React.useState<Review[]>([]);
  const [editingReviewId, setEditingReviewId] = React.useState<string | null>(null);

  const safeDateString = (value: any) => {
    if (!value) return 'TBA';
    if (value && typeof value === 'object' && 'seconds' in value) {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    let dVal = value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      dVal = value + 'T00:00:00';
    }
    const d = new Date(dVal);
    if (isNaN(d.getTime())) {
      if (typeof value === 'string' && value.length > 5 && isNaN(Number(value))) {
        return value;
      }
      return 'TBA';
    }
    return d.toLocaleDateString();
  };

  const safeDateLongString = (value: any) => {
    if (!value || value === 'TBA' || value === 'undefined') return 'TBA (Itinerary Pending)';
    if (value && typeof value === 'object' && 'seconds' in value) {
      return new Date(value.seconds * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    let dVal = value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      dVal = value + 'T00:00:00';
    }
    const d = new Date(dVal);
    if (isNaN(d.getTime())) {
      if (typeof value === 'string' && value.length > 5 && isNaN(Number(value))) {
        return value;
      }
      return 'TBA (Itinerary Pending)';
    }
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  React.useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'reviews'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMyReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Review)));
    }, (err) => console.error("MemberPortal reviews snapshot error:", err));
    return () => unsubscribe();
  }, [user]);

  const [showWaiverModal, setShowWaiverModal] = React.useState(false);
  const [waiverSignature, setWaiverSignature] = React.useState('');
  const [isSigningWaiver, setIsSigningWaiver] = React.useState(false);

  const handleSignWaiver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waiverSignature.trim() || !user) return;
    setIsSigningWaiver(true);
    try {
      await updateProfile({ waiverSigned: true, waiverSignature });
      setShowWaiverModal(false);
    } catch (e) {
      console.error("Failed to sign waiver", e);
    } finally {
      setIsSigningWaiver(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewContent.trim() || !user) return;
    setIsSubmittingReview(true);
    try {
      let uploadedImageUrl = reviewImageUrl;

      // Only upload if it's a new file
      if (reviewImage) {
        uploadedImageUrl = await uploadFile(reviewImage, `reviews/${user.uid}_${Date.now()}`);
      }

      if (editingReviewId) {
        await updateDoc(doc(db, 'reviews', editingReviewId), {
          content: reviewContent,
          image: uploadedImageUrl || '',
          updatedAt: serverTimestamp(),
          status: 'pending' // Reset to pending if edited
        });
      } else {
        await addDoc(collection(db, 'reviews'), {
          userId: user.uid,
          userName: profile?.displayName || user.displayName || 'Anonymous Explorer',
          userAvatar: profile?.photoURL || user.photoURL || '',
          userTier: profile?.membershipTier || 'Syndicate Member',
          content: reviewContent,
          image: uploadedImageUrl || '',
          status: 'pending',
          createdAt: serverTimestamp(),
          type: 'member_dispatch'
        });
      }

      setReviewContent('');
      setReviewImage(null);
      setReviewImageUrl(null);
      setEditingReviewId(null);
      setShowReviewToast(true);
      setTimeout(() => setShowReviewToast(false), 5000);
    } catch (error) {
      console.error("Failed to submit review:", error);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!confirm('Are you sure you want to delete this dispatch?')) return;
    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
    } catch (error) {
      console.error("Failed to delete review:", error);
    }
  };

  const handleStartEdit = (review: any) => {
    setReviewContent(review.content);
    setReviewImageUrl(review.image || null);
    setEditingReviewId(review.id);
    // Scroll to form
    const form = document.getElementById('dispatch-form');
    form?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUpdateProfile = async () => {
    if (!newDisplayName.trim()) return;
    try {
      await updateProfile({ displayName: newDisplayName });
      setIsEditingProfile(false);
    } catch (e) {
      console.error("Failed to update profile", e);
      // Let it go unhandled or show an alert? We can just catch and log it so it doesn't cause uncaught rejection.
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (confirmCancelBookingId !== bookingId) {
      setConfirmCancelBookingId(bookingId);
      setTimeout(() => setConfirmCancelBookingId(null), 3000);
      return;
    }
    setCancellingBookingId(bookingId);
    try {
      await cancelBooking(bookingId);
    } catch (error) {
      console.error("Failed to cancel booking", error);
    } finally {
      setCancellingBookingId(null);
      setConfirmCancelBookingId(null);
    }
  };

  const handleGenerateInvite = () => {
    const inviteLink = `${window.location.origin}/syndicate?ref=${profile?.uid?.slice(0, 8)}`;
    navigator.clipboard.writeText(inviteLink);
    setShowInviteToast(true);
    setTimeout(() => setShowInviteToast(false), 3000);
  };

  const getTierIcon = (tierName?: string) => {
    const plan = membershipPlans.find(p => p.name === tierName);
    const iconName = plan?.icon || 'Shield';

    switch (iconName) {
      case 'Compass': return <Compass className="h-24 w-24" />;
      case 'Zap': return <Zap className="h-24 w-24" />;
      case 'Crown': return <Crown className="h-24 w-24" />;
      case 'Gem': return <Gem className="h-24 w-24" />;
      default: return <Shield className="h-24 w-24" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ows-sand flex items-center justify-center p-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
            <Shield className="h-10 w-10 text-ows-teal animate-spin" />
          </div>
          <h2 className="text-xl font-display font-light uppercase tracking-[0.4em] text-ows-dark">Decrypting Profile</h2>
          <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Synchronizing with Syndicate Command...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login?redirect=/member-portal" replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-ows-sand flex items-center justify-center p-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
            <Compass className="h-10 w-10 text-ows-teal animate-spin-slow" />
          </div>
          <h2 className="text-xl font-display font-light uppercase tracking-[0.4em] text-ows-dark">Profile Synchronizing</h2>
          <p className="text-xs text-ows-dark/60 italic font-serif">Awaiting clearance from Syndicate Command...</p>
          <p className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40 mt-4">If this takes longer than 30 seconds, please refresh or contact command center.</p>
          <button
            onClick={() => signOut()}
            className="mt-8 text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 hover:text-ows-dark transition-all"
          >
            Abort Session
          </button>
        </div>
      </div>
    );
  }

  if (profile.membershipStatus !== 'active' && !isAdmin) {
    if (profile.membershipStatus === 'pending') {
      return (
        <div className="min-h-screen bg-ows-sand flex items-center justify-center p-6">
          <div className="text-center space-y-8 max-w-lg bg-white p-12 rounded-[3rem] shadow-2xl border border-ows-teal/5">
            <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-12 w-12 text-ows-teal" />
            </div>
            <h2 className="text-3xl font-display font-light uppercase tracking-widest text-ows-dark">Authorization Pending</h2>
            <p className="text-sm text-ows-dark/60 italic font-serif leading-relaxed">
              "Your credentials are being authenticated by command. Await admin approval for full access to the Syndicate."
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={signOut}
                className="px-10 py-5 bg-ows-dark/5 text-ows-dark text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-dark hover:text-white transition-all rounded-xl"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-ows-sand flex items-center justify-center p-6">
        <div className="text-center space-y-8 max-w-lg bg-white p-12 rounded-[3rem] shadow-2xl border border-ows-teal/5">
          <div className="w-24 h-24 bg-ows-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-12 w-12 text-ows-orange" />
          </div>
          <h2 className="text-3xl font-display font-light uppercase tracking-widest text-ows-dark">Membership Required</h2>
          <p className="text-sm text-ows-dark/60 italic font-serif leading-relaxed">
            "Your credentials are valid, but your Syndicate access has not been activated. To access the live telemetry, classified dispatches, and priority support, you must choose a membership tier."
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/syndicate"
              className="px-10 py-5 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-dark transition-all rounded-xl flex items-center justify-center gap-2"
            >
              View Tiers <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => signOut()}
              className="px-10 py-5 bg-ows-sand text-ows-dark text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-teal hover:text-white transition-all rounded-xl"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const exclusiveVideos = explorerTV.slice(0, 3);

  // Filter orders for vessels
  const vesselOrders = orders.filter(order =>
    order.type === 'vessel_reservation' ||
    order.items.some(item =>
      item.category === 'Vessels' ||
      item.category === 'Boards' ||
      item.name?.toLowerCase().includes('vessel') ||
      item.name?.toLowerCase().includes('board') ||
      item.name?.toLowerCase().includes('endurance')
    )
  );

  const otherOrders = orders.filter(order => !vesselOrders.includes(order));

  const getAdventureItems = () => {
    return [
      {
        label: 'Expeditions',
        path: '/expeditions',
        icon: Compass,
        locked: !isAdmin && profile?.membershipTier === 'Prospect'
      },
      {
        label: 'Training Lessons',
        path: '/coaching',
        icon: Compass,
        locked: false
      },
      {
        label: 'Team Buildings',
        path: '/team-building',
        icon: Users,
        locked: false
      },
      {
        label: 'Lake Crossing',
        path: '/day-trips',
        icon: MapIcon,
        locked: !isAdmin && profile?.membershipTier === 'Prospect'
      },
      {
        label: 'Baja',
        path: isAdmin || profile?.deploymentCleared ? '/day-trips' : '#',
        icon: Navigation,
        locked: !isAdmin && (!profile?.deploymentCleared || profile?.membershipTier === 'Prospect')
      }
    ];
  };

  const renderAdventureItem = (item: any) => {
    const isWaiverRequired = !profile?.waiverSigned && !isAdmin;
    return (
      <Link
        key={item.label}
        to={item.locked ? '#' : item.path}
        className={`bg-white p-6 rounded-3xl border border-ows-teal/5 shadow-md flex flex-col items-center text-center transition-all ${item.locked ? 'opacity-40 bg-gray-50 cursor-not-allowed border-black/5' : 'hover:border-ows-teal/20 hover:shadow-lg hover:-translate-y-1 cursor-pointer duration-300'}`}
        onClick={(e) => {
          if (item.locked) {
            e.preventDefault();
          } else if (isWaiverRequired) {
            e.preventDefault();
            setShowWaiverModal(true);
          }
        }}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-all ${item.locked ? 'bg-gray-100 text-gray-400' : 'bg-ows-sand text-ows-teal group-hover:bg-ows-teal group-hover:text-white'}`}>
          <item.icon className="h-5 w-5" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/60">{item.label}</span>
        {item.locked ? (
          <span className="text-[8px] text-red-500 font-bold uppercase tracking-widest mt-2 flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" /> Locked
          </span>
        ) : isWaiverRequired ? (
          <span className="text-[8px] text-red-500 font-bold uppercase tracking-widest mt-2 flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" /> Waiver Req
          </span>
        ) : (
          <span className="text-[8px] text-green-600 font-bold uppercase tracking-widest mt-2 flex items-center gap-1">
            Active
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-ows-sand pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="relative group/avatar">
              <div className="w-24 h-24 rounded-full border-2 border-ows-teal/20 overflow-hidden bg-ows-sand shadow-xl">
                {profile.photoURL ? (
                  <img src={profile.photoURL} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ows-teal/20">
                    <User className="h-12 w-12" />
                  </div>
                )}
              </div>
              <label className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white rounded-full opacity-0 group-hover/avatar:opacity-100 cursor-pointer transition-all">
                <Camera className="h-5 w-5 mb-1" />
                <span className="text-[6px] font-bold uppercase tracking-widest">Update</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const url = await uploadFile(file, `avatars/${user.uid}`);
                      await updateProfile({ photoURL: url });
                    } catch (err) {
                      console.error("Failed to upload avatar", err);
                    }
                  }}
                />
              </label>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-ows-teal text-white text-[8px] font-bold uppercase tracking-widest rounded-full">
                  {profile.membershipTier || 'Syndicate Member'}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">
                  Member since {profile.joinedAt ? new Date(profile.joinedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-4">
                {isEditingProfile ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      className="bg-white border border-ows-teal/20 px-4 py-2 rounded-lg text-2xl font-display uppercase tracking-widest text-ows-dark focus:outline-none focus:border-ows-teal"
                      autoFocus
                    />
                    <button
                      onClick={handleUpdateProfile}
                      className="p-2 bg-ows-teal text-white rounded-lg hover:bg-ows-teal/90 transition-all"
                    >
                      <Save className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setIsEditingProfile(false)}
                      className="p-2 bg-ows-sand text-ows-dark rounded-lg hover:bg-ows-sand/90 transition-all"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <h1 className="text-5xl font-display font-light uppercase tracking-widest text-ows-dark flex items-center gap-4 flex-wrap">
                    Welcome, <span className="text-ows-teal">{profile.displayName?.split(' ')[0]}</span>
                    {profile.isVerified && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-ows-teal/10 rounded-full border border-ows-teal/20" title="Identity Verified through Payment">
                        <ShieldCheck className="h-3 w-3 text-ows-teal" />
                        <span className="text-[8px] font-bold uppercase tracking-widest text-ows-teal">Identity Secured</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setNewDisplayName(profile.displayName || '');
                        setIsEditingProfile(true);
                      }}
                      className="p-2 text-ows-dark/20 hover:text-ows-teal transition-all"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </h1>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => signOut()}
              className="flex items-center gap-2 px-6 py-3 bg-white text-ows-dark text-[10px] font-bold uppercase tracking-widest border border-ows-teal/10 hover:bg-red-50 hover:text-red-500 transition-all rounded-xl"
            >
              <LogOut className="h-4 w-4" /> Terminate Session
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column: Profile & Orders */}
          <div className="lg:col-span-2 space-y-12">
            {/* Dispatches & Notifications Section */}
            <section className="bg-white p-8 md:p-12 rounded-[3.5rem] border border-ows-teal/10 shadow-2xl relative overflow-hidden">
              <div className="flex items-center gap-4 mb-8 text-ows-teal">
                <Mail className="h-6 w-6" />
                <h2 className="text-xl font-display font-light uppercase tracking-widest text-ows-dark m-0">Dispatches & Notifications</h2>
              </div>

              <div className="space-y-4">
                {/* Generate Welcome notification if member */}
                {profile.membershipTier && (
                  <div className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-dark/5 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-sm font-bold uppercase tracking-widest text-ows-dark flex items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-ows-teal" /> Syndicate Welcome Packet
                        </h3>
                        <p className="text-xs text-ows-dark/60 leading-relaxed font-serif max-w-xl mb-4">
                          Your Syndicate membership is active. Review the intelligence and secure your digital liability waiver before deployment.
                        </p>
                        <div className="flex items-center gap-4">
                          {profile.waiverSigned ? (
                            <div className="inline-flex py-3 px-4 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-widest rounded-xl items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-ows-teal" /> Waiver Executed
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowWaiverModal(true)}
                              className="inline-flex py-3 px-4 bg-ows-teal/10 text-ows-teal text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-ows-teal/20 transition-colors items-center gap-2 border border-ows-teal/20"
                            >
                              <FileText className="h-3 w-3" /> Execute Digital Waiver
                            </button>
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 whitespace-nowrap">
                        {new Date(profile.joinedAt || Date.now()).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
                {userBookings.map((b) => {
                  const s = expeditionSlots.find(slot => slot.id === b.slotId);
                  return (
                    <div key={`dispatch-${b.id}`} className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-dark/5 shadow-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-bold uppercase tracking-widest text-ows-dark flex items-center gap-2 mb-2">
                            <Calendar className="h-4 w-4 text-ows-teal" /> Booking Confirmed: {b.serviceName || s?.title || s?.type || b.type || 'Expedition'}
                          </h3>
                          <p className="text-xs text-ows-dark/60 leading-relaxed font-serif max-w-xl">
                            Your spot in the expedition has been logged. An ICS invite and your mission packet are attached and your position is secured.
                          </p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 whitespace-nowrap">
                          {safeDateString(b.timestamp || b.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {orders.map((o: any) => {
                  const isVesselOrder = o.items?.some((item: any) =>
                    item.category === 'Vessels' ||
                    item.category === 'Boards' ||
                    item.name?.toLowerCase().includes('board') ||
                    item.name?.toLowerCase().includes('vessel') ||
                    item.name?.toLowerCase().includes('c2') ||
                    item.name?.toLowerCase().includes('endurance')
                  );
                  const totalFormatted = (o.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  return (
                    <div key={`dispatch-${o.id}`} className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-dark/5 shadow-sm space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="block text-[8px] font-mono text-ows-teal/60 font-bold uppercase tracking-widest mb-1">Receipt Filed</span>
                          <h3 className="text-xs font-bold uppercase tracking-widest text-ows-dark flex items-center gap-2 mb-2">
                            <ShoppingBag className="h-4 w-4 text-ows-teal" /> Order Invoice: #{o.id}
                          </h3>
                          <p className="text-xs text-ows-dark/60 leading-relaxed font-serif max-w-xl">
                            Receipt for ${totalFormatted} ({o.items?.length || 0} items) has been logged and filed to your active profile.
                          </p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 whitespace-nowrap">
                          {new Date(o.timestamp).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Customer Staged Shipping Tracking Status */}
                      {isVesselOrder && (
                        <div className="p-4 bg-white rounded-2xl border border-ows-teal/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div className="space-y-1">
                            <span className="text-[8px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 bg-ows-orange text-white rounded font-mono">
                              Deferred Logistics Protocol
                            </span>
                            <h4 className="text-xs font-bold text-ows-dark uppercase tracking-wide">Board Delivery & Staged Shipping Status</h4>

                            {!o.shippingQuoteGenerated ? (
                              <p className="text-[10px] text-ows-dark/50 leading-relaxed max-w-lg">
                                Calculating custom rates... Shipping costs will be generated and invoiced via secure Stripe link once your board arrives at our <strong>Toronto Downtown HQ</strong>. Local headquarters pickup is available free of charge.
                              </p>
                            ) : o.shippingQuotePaid ? (
                              <p className="text-[10px] text-green-600 font-bold leading-relaxed">
                                ✓ Shipping Settled: {o.localPickupRequested ? 'Arranged for Downtown Toronto HQ Pickup ($0.00)' : `Custom ground rate of $${o.shippingQuoteAmount} has been paid via Stripe.`}
                              </p>
                            ) : (
                              <p className="text-[10px] text-amber-600 font-semibold leading-relaxed">
                                ⚠️ Custom Shipping Invoice calculated at <strong>${o.shippingQuoteAmount} CAD</strong>. {o.localPickupRequested ? 'Local Downtown HQ Pickup Selected.' : 'Stripe Invoice Link generated below.'}
                              </p>
                            )}
                          </div>

                          {o.shippingQuoteGenerated && !o.shippingQuotePaid && !o.localPickupRequested && (
                            <a
                              href={o.shippingQuoteInvoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="px-4 py-2 bg-ows-teal text-white text-[9px] font-bold uppercase tracking-widest rounded-xl hover:bg-ows-teal/90 transition-all font-mono whitespace-nowrap text-center"
                            >
                              Pay Stripe Invoice ↗
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {(!profile.membershipTier && userBookings.length === 0 && orders.length === 0) && (
                  <div className="py-12 text-center text-ows-dark/40 font-mono text-[10px] uppercase tracking-widest">
                    No communications generated yet.
                  </div>
                )}
              </div>
            </section>

            {/* If Prospect, show Book an Adventure as a highly visible, gorgeous sales-accented top block */}
            {profile?.membershipTier === 'Prospect' && (
              <section className="bg-gradient-to-br from-white to-ows-teal/[0.02] p-8 md:p-12 rounded-[3.5rem] border border-ows-teal/20 shadow-2xl relative overflow-hidden mb-12">
                <div className="absolute top-0 right-0 py-3 px-6 bg-ows-teal text-white text-[9px] font-mono uppercase tracking-[0.25em] rounded-bl-3xl">
                  EXPLORATION ACCESS GATE
                </div>
                <div className="mb-6">
                  <span className="text-[9px] font-bold uppercase tracking-[0.3em] font-mono text-ows-teal mb-3 block">RECOMMENDED OPERATION</span>
                  <h2 className="text-3xl font-display font-light uppercase tracking-widest flex items-center gap-4 text-ows-dark">
                    <Zap className="h-6 w-6 text-ows-teal animate-pulse" /> Book an Adventure
                  </h2>
                  <p className="mt-3 text-xs text-ows-dark/50 font-serif leading-relaxed max-w-2xl">
                    Welcome to the Fleet, Prospect. As a free Prospect member, you can now schedule face-to-face training lessons and corporate team buildings under the modules unlocked below. This is your gateway link to step onto our hull fleets.
                  </p>
                  <div className="mt-6 p-5 bg-ows-teal/5 border border-ows-teal/10 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl">
                    <div className="space-y-1">
                      <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-ows-teal block font-mono">🎯 UPGRADE LOCK DETECTED</span>
                      <span className="text-[10px] text-ows-dark/70 font-sans leading-relaxed block text-center sm:text-left">
                        Our premium Ocean Expeditions and Lake Crossings are restricted. Activate your membership to unlock full access.
                      </span>
                    </div>
                    <Link
                      to="/membership-checkout?planId=syndicate-ultimate"
                      className="px-6 py-3 bg-ows-teal hover:bg-ows-dark text-white text-[9px] font-bold uppercase tracking-widest rounded-xl transition-all shrink-0 flex items-center gap-2 shadow-md shadow-ows-teal/10"
                    >
                      <IdCard className="h-3.5 w-3.5" /> Syndicate Activation
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
                  {getAdventureItems().map((item) => renderAdventureItem(item))}
                </div>
              </section>
            )}

            {/* Vessel Reservations Section */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-display font-light uppercase tracking-widest flex items-center gap-3">
                  <Navigation className="h-5 w-5 text-ows-teal" /> Vessel Reservations
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {vesselOrders.map((order, idx) => (
                  <motion.div
                    key={`vessel-order-${order.id}-${idx}`}
                    whileHover={{ y: -5 }}
                    className="bg-white p-8 rounded-[2rem] border border-ows-teal/5 shadow-xl relative overflow-hidden group"
                  >
                    <div className="absolute top-0 right-0 p-6">
                      <span className="px-3 py-1 bg-green-100 text-green-600 text-[8px] font-bold uppercase tracking-widest rounded-full">
                        {order.status}
                      </span>
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal mb-2">Vessel Order</p>
                    <h3 className="text-2xl font-display font-light uppercase tracking-widest mb-6">
                      {order.items.find(i => i.category === 'Boards' || i.category === 'Vessels')?.name || 'OWS Vessel'}
                    </h3>

                    <div className="space-y-3 mb-8">
                      <div className="flex items-center gap-3 text-ows-dark/60 text-xs">
                        <Clock className="h-4 w-4" />
                        <span>Ordered {new Date(order.timestamp || order.createdAt || 0).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-3 text-ows-dark/60 text-xs">
                        <ShoppingBag className="h-4 w-4" />
                        <span>Order ID: <span style={{ fontSize: "8px" }}>{order.id ? String(order.id).toUpperCase() : 'N/A'}</span></span>
                      </div>
                    </div>

                    {order.shippingAddress && (
                      <div className="p-4 bg-ows-sand/50 rounded-xl mb-6">
                        <p className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40 mb-1">Shipping to</p>
                        <p className="text-[10px] text-ows-dark font-medium">{order.shippingAddress.city}, {order.shippingAddress.street}</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <p className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40">Included Gear:</p>
                      <div className="flex flex-wrap gap-2">
                        {order.items?.flatMap((item: any) => Array.isArray(item.items) ? item.items : [item.name || item])?.map((gear: any, idx: number) => (
                          <span key={`gear-${order.id}-${idx}`} className="px-2 py-1 bg-ows-teal/5 text-[8px] font-bold uppercase tracking-widest text-ows-teal rounded-md border border-ows-teal/10">
                            {gear}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {vesselOrders.length === 0 && (
                  <div className="col-span-full py-20 bg-white/50 border border-dashed border-ows-teal/20 rounded-[2rem] flex flex-col items-center justify-center text-center px-6">
                    <p className="text-sm text-ows-dark/40 italic font-serif mb-6">"No vessels currently reserved."</p>
                    <Link to="/expedition-cx2#configurator" className="px-8 py-4 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-widest rounded-xl">Configure Your Vessel</Link>
                  </div>
                )}
              </div>
            </section>

            {/* Booked Expeditions Section */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-display font-light uppercase tracking-widest flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-ows-teal" /> Booked Expeditions
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {userBookings.map((booking, idx) => {
                  const slot = expeditionSlots.find(s => s.id === booking.slotId);
                  return (
                    <motion.div
                      key={`booking-${booking.id}-${idx}`}
                      whileHover={{ y: -5 }}
                      className="bg-white p-8 rounded-[2rem] border border-ows-teal/5 shadow-xl relative overflow-hidden group"
                    >
                      <div className="absolute top-0 right-0 p-6">
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          disabled={cancellingBookingId === booking.id}
                          className={`text-[8px] font-bold uppercase tracking-widest transition-all disabled:opacity-50 ${confirmCancelBookingId === booking.id ? 'text-red-500 animate-pulse' : 'text-ows-dark/20 hover:text-red-500'}`}
                        >
                          {cancellingBookingId === booking.id ? 'Cancelling...' : confirmCancelBookingId === booking.id ? 'Confirm Cancel' : 'Cancel Booking'}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">
                          {(booking.serviceName || booking.type || 'Expedition').replace(/-/g, ' ')}
                        </p>
                        {booking.status === 'pending' && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 text-[8px] uppercase tracking-widest font-bold rounded-full">Pending</span>}
                        {booking.status === 'waitlist' && <span className="px-2 py-0.5 bg-ows-orange/10 text-ows-orange text-[8px] uppercase tracking-widest font-bold rounded-full">Waitlist</span>}
                        {booking.status === 'confirmed' && <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-[8px] uppercase tracking-widest font-bold rounded-full">Confirmed</span>}
                        {booking.status === 'cancelled' && <span className="px-2 py-0.5 bg-red-500/10 text-red-500 text-[8px] uppercase tracking-widest font-bold rounded-full">Cancelled</span>}
                      </div>
                      <h3 className="text-2xl font-display font-light uppercase tracking-widest mb-6 font-semibold">
                        {slot ? safeDateLongString(slot.date) : safeDateLongString(booking.date)}
                      </h3>

                      <div className="space-y-3 mb-8">
                        <div className="flex items-center gap-3 text-ows-dark/60 text-xs">
                          <Clock className="h-4 w-4" />
                          <span>{booking.time || slot?.time || 'Scheduled Time'} — {slot?.type === 'coaching' || booking.serviceName === 'Private Coaching' ? '1-3 Hours' : '4-6 Hours'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-ows-dark/60 text-xs">
                          <MapPin className="h-4 w-4" />
                          <span>{booking.instructions?.meet || (slot?.type === 'night-raid' || booking.serviceName === 'Night Raid' ? 'Toronto Islands' : 'Waterfront HQ')}</span>
                        </div>
                      </div>

                      {booking.instructions && (booking.instructions.meet || booking.instructions.expect || booking.instructions.bring || booking.instructions.clothes) && (
                        <div className="mb-6 bg-[#f0faf9] p-5 rounded-2xl border border-ows-teal/10 space-y-3">
                          <h4 className="text-[10px] font-bold uppercase tracking-widest text-[#0d3a40]">Mission Itinerary & Instructions</h4>

                          <div className="grid grid-cols-1 gap-2 text-[11px] leading-relaxed">
                            {booking.instructions.meet && (
                              <div>
                                <span className="font-bold text-ows-dark/60 uppercase text-[9px]">Where to Meet: </span>
                                <span className="text-ows-dark">{booking.instructions.meet}</span>
                              </div>
                            )}
                            {booking.instructions.clothes && (
                              <div>
                                <span className="font-bold text-ows-dark/60 uppercase text-[9px]">Wear: </span>
                                <span className="text-ows-dark">{booking.instructions.clothes}</span>
                              </div>
                            )}
                            {booking.instructions.bring && (
                              <div>
                                <span className="font-bold text-ows-dark/60 uppercase text-[9px]">Bring: </span>
                                <span className="text-ows-dark">{booking.instructions.bring}</span>
                              </div>
                            )}
                            {booking.instructions.expect && (
                              <div>
                                <span className="font-bold text-ows-dark/60 uppercase text-[9px]">Expect: </span>
                                <span className="text-ows-dark">{booking.instructions.expect}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="p-4 bg-ows-sand/50 rounded-xl">
                        <p className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40 mb-1">Status</p>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${booking.status === 'confirmed' ? 'bg-green-500' : booking.status === 'pending' || booking.status === 'waitlist' ? 'bg-ows-orange' : 'bg-red-500'}`} />
                          <p className="text-[10px] text-ows-dark font-bold uppercase tracking-widest">
                            {booking.status === 'confirmed' ? 'Confirmed & Secured'
                              : booking.status === 'waitlist' ? 'Waitlisted'
                                : booking.status === 'pending' ? 'Pending Approval'
                                  : 'Cancelled'}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}

                {userBookings.length === 0 && (
                  <div className="col-span-full py-12 bg-white/50 border border-dashed border-ows-teal/20 rounded-[2rem] flex flex-col items-center justify-center text-center px-6">
                    <p className="text-sm text-ows-dark/40 italic font-serif mb-4">"No active expeditions booked."</p>
                  </div>
                )}
              </div>
            </section>

            {/* NEW: Book an Adventure Section */}
            {profile?.membershipTier !== 'Prospect' && (
              <section>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-display font-light uppercase tracking-widest flex items-center gap-3">
                    <Zap className="h-5 w-5 text-ows-teal" /> Book an Adventure
                  </h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {getAdventureItems().map((item) => renderAdventureItem(item))}
                </div>
              </section>
            )}

            {/* Other Orders Section */}
            {otherOrders.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-display font-light uppercase tracking-widest flex items-center gap-3">
                    <ShoppingBag className="h-5 w-5 text-ows-teal" /> Order History
                  </h2>
                </div>

                <div className="space-y-4">
                  {otherOrders.map((order, idx) => (
                    <div key={`other-order-${order.id}-${idx}`} className="bg-white p-6 rounded-3xl border border-ows-teal/5 shadow-lg flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="w-12 h-12 bg-ows-sand rounded-xl flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6 text-ows-teal" />
                        </div>
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-widest">
                            {order.items.map(i => i.name).join(', ')}
                          </h4>
                          <p className="text-[8px] text-ows-dark/40 uppercase tracking-widest">
                            {new Date(order.timestamp).toLocaleDateString()} • ID: {order.id ? String(order.id).slice(0, 8).toUpperCase() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-ows-teal">${order.total.toFixed(2)}</p>
                        <span className="text-[8px] font-bold uppercase tracking-widest text-green-600">{order.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Live Tracking Section */}
            {trackingConfig.enabled && (
              <section className="bg-white p-8 md:p-12 rounded-[3rem] border border-ows-teal/10 shadow-2xl overflow-hidden relative group">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                  <Navigation className="h-32 w-32 text-ows-teal" />
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 relative z-10">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-green-600">Live Satellite Feed</span>
                    </div>
                    <h2 className="text-3xl font-display font-light uppercase tracking-widest text-ows-dark flex items-center gap-4">
                      <Navigation className="h-8 w-8 text-ows-teal" /> Live Tracking
                    </h2>
                  </div>
                  <div className="flex items-center gap-4">
                    <a
                      href={trackingConfig.liveTrackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-ows-orange text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-ows-orange/90 transition-all flex items-center gap-2"
                    >
                      LiveTrack View <ExternalLink className="h-3 w-3" />
                    </a>
                    <a
                      href={trackingConfig.mapShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-ows-sand text-ows-dark text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-ows-teal hover:text-white transition-all flex items-center gap-2"
                    >
                      Full MapShare <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-3 aspect-video w-full bg-ows-sand rounded-[2rem] overflow-hidden border border-ows-teal/5 shadow-inner relative">
                    <iframe
                      src={trackingConfig.mapShareUrl}
                      className="w-full h-full border-none"
                      title="Garmin MapShare"
                      allow="geolocation"
                    />
                    <div className="absolute inset-0 bg-ows-dark/40 backdrop-blur-[2px] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-500">
                      <a
                        href={trackingConfig.mapShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-4 bg-white text-ows-dark text-[10px] font-bold uppercase tracking-widest rounded-xl shadow-2xl flex items-center gap-2 hover:bg-ows-teal hover:text-white transition-all"
                      >
                        <ExternalLink className="h-4 w-4" /> Open Interactive Map
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 bg-ows-sand/50 rounded-2xl border border-ows-teal/5">
                    <p className="text-[8px] font-bold uppercase tracking-widest text-ows-teal mb-2">Update Frequency</p>
                    <p className="text-xs font-serif italic text-ows-dark/60">Position updates every 10 minutes during active movement.</p>
                  </div>
                  <div className="p-6 bg-ows-sand/50 rounded-2xl border border-ows-teal/5">
                    <p className="text-[8px] font-bold uppercase tracking-widest text-ows-teal mb-2">Two-Way Comms</p>
                    <p className="text-xs font-serif italic text-ows-dark/60">Members can send messages directly to the team via the map portal.</p>
                  </div>
                  <div className="p-6 bg-ows-sand/50 rounded-2xl border border-ows-teal/5">
                    <p className="text-[8px] font-bold uppercase tracking-widest text-ows-teal mb-2">Global Coverage</p>
                    <p className="text-xs font-serif italic text-ows-dark/60">Tracking remains active even in the most remote dead zones.</p>
                  </div>
                </div>
              </section>
            )}

            {/* Dispatch Social Proof Section */}
            <section id="dispatch-form" className="bg-white p-8 md:p-12 rounded-[3.5rem] border border-ows-teal/10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                <MessageSquare className="h-32 w-32 text-ows-teal" />
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-3xl font-display font-light uppercase tracking-widest">
                    {editingReviewId ? 'Edit Your Story' : 'Submit Your Story'}
                  </h2>
                  {editingReviewId && (
                    <button
                      onClick={() => {
                        setEditingReviewId(null);
                        setReviewContent('');
                        setReviewImageUrl(null);
                        setReviewImage(null);
                      }}
                      className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 hover:text-ows-teal underline"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
                <p className="text-xs text-ows-dark/60 font-serif italic mb-8 max-w-lg">
                  "Your experiences on the open water inspire the next generation of explorers. Share your crossing story or testimonial to be featured on our social proof wall."
                </p>

                <div className="space-y-6">
                  <div className="relative">
                    <textarea
                      value={reviewContent}
                      onChange={(e) => setReviewContent(e.target.value)}
                      placeholder="Describe your most recent expedition or your experience with OWS..."
                      className="w-full h-32 bg-ows-sand/20 rounded-[2rem] p-6 text-xs font-mono uppercase tracking-widest border-none focus:ring-2 focus:ring-ows-teal transition-all placeholder:text-ows-dark/20"
                    />

                    <div className="mt-4 flex flex-wrap gap-4">
                      {reviewImageUrl ? (
                        <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-ows-teal/20">
                          <img src={reviewImageUrl} className="w-full h-full object-cover" />
                          <button
                            onClick={() => {
                              setReviewImage(null);
                              setReviewImageUrl(null);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="w-24 h-24 rounded-xl border-2 border-dashed border-ows-teal/20 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-ows-teal/5 transition-all">
                          <Camera className="h-5 w-5 text-ows-teal/40" />
                          <span className="text-[8px] font-bold uppercase tracking-widest text-ows-teal/40 text-center px-2">Attach Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setReviewImage(file);
                                setReviewImageUrl(URL.createObjectURL(file));
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-[8px] font-mono font-bold uppercase tracking-widest text-ows-dark/30">
                      * Reviews {editingReviewId ? 'will be re-vetted' : 'are subject to admin vetting'} before publishing.
                    </p>
                    <button
                      onClick={handleSubmitReview}
                      disabled={isSubmittingReview || !reviewContent.trim()}
                      className="px-8 py-4 bg-[#141414] text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl hover:bg-ows-teal transition-all disabled:opacity-50 flex items-center gap-3"
                    >
                      {isSubmittingReview ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Transmitting...
                        </>
                      ) : showReviewToast ? (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          {editingReviewId ? 'Update Received' : 'Story Received'}
                        </>
                      ) : (
                        <>
                          {editingReviewId ? 'Update Dispatch' : 'Submit Dispatch'}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* My Reviews List */}
                {myReviews.length > 0 && (
                  <div className="mt-16 pt-12 border-t border-ows-teal/10">
                    <h3 className="text-xl font-display font-light uppercase tracking-widest mb-8">My Dispatches</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {myReviews.map((review) => (
                        <div key={review.id} className="bg-ows-sand/20 p-6 rounded-3xl border border-ows-teal/5 flex gap-4">
                          {review.image && (
                            <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
                              <img src={review.image} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-grow min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <span className={`px-2 py-0.5 rounded text-[7px] font-bold uppercase tracking-widest ${review.status === 'approved' ? 'bg-green-100 text-green-600' :
                                review.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                  'bg-ows-orange/10 text-ows-orange'
                                }`}>
                                {review.status}
                              </span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleStartEdit(review)}
                                  className="text-ows-teal hover:text-ows-dark transition-all"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteReview(review.id)}
                                  className="text-red-500/40 hover:text-red-500 transition-all"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <p className="text-[10px] text-ows-dark/60 font-serif italic line-clamp-2 leading-relaxed">
                              "{review.content}"
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Exclusive Content Section */}
            <section className="space-y-12">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-display font-light uppercase tracking-widest flex items-center gap-3">
                  <Video className="h-5 w-5 text-ows-teal" /> Syndicate Intelligence
                </h2>
                <div className="px-4 py-2 bg-ows-teal/10 rounded-full border border-ows-teal/20 flex items-center gap-2">
                  <div className="w-2 h-2 bg-ows-teal rounded-full animate-pulse" />
                  <span className="text-[8px] font-bold uppercase tracking-widest text-ows-teal">Classified Feed</span>
                </div>
              </div>

              {/* Elite Restricted Content */}
              {profile.automationTags?.includes('ELITE_RANK') && (
                <div className="p-8 bg-ows-orange/5 border border-ows-orange/20 rounded-[2.5rem] mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Crown className="h-6 w-6 text-ows-orange" />
                    <h3 className="text-xs font-bold uppercase tracking-[0.4em] text-ows-orange">Elite Access Enabled</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h4 className="text-xl font-display font-light uppercase tracking-widest">Baja Qualification Protocols</h4>
                      <p className="text-xs text-ows-dark/60 italic font-serif">"You have reached the rank required for open-ocean deployment. Access the navigation logs below to begin your physical conditioning path."</p>
                      <Link to="/day-trips" className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ows-orange hover:gap-4 transition-all">View Deployment Path <ArrowRight className="h-3 w-3" /></Link>
                    </div>
                    <div className="bg-ows-dark rounded-2xl p-6 text-white text-center flex flex-col items-center justify-center border border-ows-orange/20">
                      <Gem className="h-8 w-8 text-ows-orange mb-4" />
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-1">Private Channel</p>
                      <p className="text-[8px] opacity-40 uppercase tracking-widest mb-4">Encrypted Signal Active</p>
                      <button className="px-6 py-3 bg-ows-orange text-white text-[8px] font-bold uppercase tracking-widest rounded-lg hover:bg-white hover:text-ows-orange transition-all">Direct Command Link</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {stories.slice(0, 3).map((story, i) => (
                  <motion.div
                    key={`story-${story.id}-${i}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[4/5] bg-ows-dark rounded-[2.5rem] overflow-hidden relative mb-6 shadow-xl">
                      <img
                        src={resolveMediaUrl(story.image) || undefined}
                        alt={story.title}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ows-dark via-transparent to-transparent opacity-80" />
                      <div className="absolute top-6 right-6">
                        <div className="px-3 py-1.5 bg-ows-teal/20 backdrop-blur-md border border-ows-teal/30 rounded-full flex items-center gap-2">
                          <Lock className="h-3 w-3 text-ows-teal" />
                          <span className="text-[8px] font-bold uppercase tracking-widest text-ows-teal">Classified</span>
                        </div>
                      </div>
                      <div className="absolute bottom-8 left-8 right-8">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-ows-teal mb-2">{story.category}</p>
                        <h3 className="text-xl font-display font-light uppercase tracking-widest text-white leading-tight group-hover:text-ows-teal transition-colors">{story.title}</h3>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {exclusiveVideos.map((video, idx) => (
                  <motion.div
                    key={`exclusive-video-${video.id}-${idx}`}
                    whileHover={{ scale: 1.02 }}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-video bg-ows-dark rounded-[2rem] overflow-hidden relative mb-4 shadow-xl">
                      <img
                        src={resolveMediaUrl(video.thumbnail) || "https://picsum.photos/seed/exclusive/800/450"}
                        alt={video.title}
                        className="w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-ows-teal/20 backdrop-blur-md rounded-full flex items-center justify-center border border-ows-teal/30 group-hover:scale-110 transition-all">
                          <Play className="h-6 w-6 text-white fill-white ml-1" />
                        </div>
                      </div>
                      <div className="absolute top-6 left-6">
                        <span className="px-3 py-1 bg-ows-teal text-white text-[8px] font-bold uppercase tracking-widest rounded-full">Exclusive</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-display font-light uppercase tracking-widest text-ows-dark group-hover:text-ows-teal transition-colors">{video.title}</h3>
                  </motion.div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8">
            {/* Membership Card */}
            <div className="bg-ows-dark p-10 rounded-[3m] text-white relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                {getTierIcon(profile.membershipTier)}
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-ows-teal mb-4">Membership Rank</p>
              <h3 className="text-3xl font-display font-light uppercase tracking-widest mb-8">{profile.membershipTier}</h3>

              <div className="space-y-6 mb-12">
                <div className="flex items-center justify-between text-xs border-b border-white/10 pb-4">
                  <span className="text-white/40 uppercase tracking-widest">Status</span>
                  <span className="text-green-400 font-bold uppercase">Active Account</span>
                </div>
                {profile.automationTags?.includes('CORP_MEMBER') && (
                  <div className="flex items-center justify-between text-xs border-b border-white/10 pb-4">
                    <span className="text-white/40 uppercase tracking-widest">Perks</span>
                    <span className="text-ows-teal font-bold uppercase tracking-tighter">50% ACTIVITY DISC.</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs border-b border-white/10 pb-4">
                  <span className="text-white/40 uppercase tracking-widest">Rank Type</span>
                  <span className={`font-bold uppercase tracking-widest ${profile.automationTags?.includes('ELITE_RANK') ? 'text-ows-orange' : 'text-white/40'}`}>
                    {profile.automationTags?.includes('ELITE_RANK') ? 'ELITE_DEPLOYMENT' : 'STANDARD_CORPS'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs border-b border-white/10 pb-4">
                  <span className="text-white/40 uppercase tracking-widest">Member ID</span>
                  <span className="font-mono">#OWS-{profile.uid.slice(0, 4).toUpperCase()}-2026</span>
                </div>
              </div>

              {/* NEW: Financial Summary */}
              <div className="mb-12 p-8 bg-white/5 rounded-3xl border border-white/10 shadow-inner">
                <div className="flex items-center gap-3 mb-6">
                  <Gem className="h-4 w-4 text-ows-teal" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.4em] text-white">Financial Overview</span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] text-white/40 uppercase tracking-widest">Total Value Invested</span>
                    <span className="text-2xl font-display text-white tracking-widest">${(profile.financials?.lifetimeSpend || 0).toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-white/10 h-1 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-ows-teal"
                      style={{ width: `${Math.min((profile.financials?.lifetimeSpend || 0) / 5000 * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-[7px] text-white/20 uppercase tracking-widest leading-relaxed">
                    Financial history used for Elite rank vetting and seniority prioritization in production Batch 001.
                  </p>
                </div>
              </div>

              {!profile.automationTags?.includes('ELITE_RANK') && profile.membershipTier !== 'Prospect' && (
                <div className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/10 mb-8">
                  <h4 className="text-[9px] font-bold uppercase tracking-widest text-ows-orange mb-2">Pathway to Elite</h4>
                  <p className="text-[8px] text-white/40 font-mono uppercase tracking-widest leading-relaxed">
                    Promotion to Elite Expeditions requires "The Syndicate: Inner Circle" status and admin vetting. Earned by merit, not bought.
                  </p>
                </div>
              )}

              {profile.membershipTier === 'Prospect' && (
                <div className="mt-8 mb-8 space-y-4">
                  <Link
                    to="/membership-checkout?planId=syndicate-ultimate"
                    className="w-full py-4 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl hover:bg-white hover:text-ows-dark transition-all flex items-center justify-center gap-2"
                  >
                    <IdCard className="h-4 w-4" /> Upgrade Membership
                  </Link>
                  <Link
                    to="/expedition-cx2"
                    className="w-full py-4 bg-transparent border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                  >
                    <Package className="h-4 w-4" /> Order OWS Board
                  </Link>
                </div>
              )}

              {profile.membershipTier !== 'Prospect' && (
                <Link
                  to="/syndicate"
                  className="w-full py-5 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl hover:bg-white hover:text-ows-dark transition-all flex items-center justify-center gap-2"
                >
                  <IdCard className="h-4 w-4" /> Manage Rank
                </Link>
              )}
            </div>

            {/* Referral Card */}
            <div className="bg-ows-teal p-10 rounded-[3rem] text-white relative overflow-hidden shadow-2xl shadow-ows-teal/20">
              <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl opacity-50" />
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-display font-light uppercase tracking-widest mb-4">Invite a Fellow Explorer</h3>
                <p className="text-white/60 text-xs leading-relaxed mb-8 font-serif italic">
                  "The horizon is wider when shared. Invite a friend to the Syndicate and both receive priority access to Batch 002."
                </p>
                <button
                  onClick={handleGenerateInvite}
                  className="w-full py-4 bg-white text-ows-dark text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-ows-orange hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  {showInviteToast ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> Link Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Generate Invite Link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showWaiverModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowWaiverModal(false)}
              className="absolute inset-0 bg-ows-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowWaiverModal(false)}
                className="absolute top-6 right-6 p-2 text-ows-dark/40 hover:text-ows-dark transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="mb-6">
                <h3 className="text-xl font-display uppercase tracking-widest text-ows-dark mb-2">Digital Member Waiver</h3>
                <p className="text-sm font-mono text-ows-dark/40 uppercase tracking-widest">Release of Liability</p>
              </div>

              <div className="prose-ows text-xs text-ows-dark/60 leading-relaxed h-64 overflow-y-auto p-6 bg-ows-grey/50 rounded-2xl border border-ows-teal/5 mb-8">
                <p>I hereby acknowledge that paddleboarding involves inherent risks, including but not limited to: water hazards, changing weather conditions, equipment failure, and drowning.</p>
                <p>I certify that I am in good physical health and possess the necessary skills to participate in this activity.</p>
                <p>I agree to follow all safety instructions provided by Open Water Syndicate guides and personnel.</p>
                <p>I fully release and discharge Open Water Syndicate, its founders, guides, and affiliates from any and all liability, claims, or demands for personal injury, property damage, or wrongful death occurring during the session.</p>
                <p>I understand that I am responsible for my own safety and the safety of any minors I am signing for.</p>
              </div>

              <form onSubmit={handleSignWaiver} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/60 ml-2">Type Full Legal Name as Signature</label>
                  <input
                    type="text"
                    required
                    value={waiverSignature}
                    onChange={(e) => setWaiverSignature(e.target.value)}
                    placeholder="Enter full name"
                    className="w-full px-6 py-4 bg-white border border-ows-teal/20 rounded-2xl text-ows-dark outline-none focus:border-ows-teal transition-colors font-serif italic text-lg"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-ows-teal/5">
                  <button
                    type="button"
                    onClick={() => setShowWaiverModal(false)}
                    className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 hover:text-ows-dark transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSigningWaiver || !waiverSignature}
                    className="px-8 py-3 bg-ows-teal text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-ows-dark transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSigningWaiver ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    Accept & Sign
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
